import React, { useRef, useState } from 'react';
import { IconButton, Tooltip } from '@material-ui/core';
import textIcon from '../../assets/text_icon.svg';
import StepConfirmation, { ConfirmationDialogProps } from './StepConfirmation';
import { Alert } from '@material-ui/lab';

/**
 * CSVUploadButton - A generic CSV upload component with schema validation
 * 
 * For Tag Values CSV format:
 * - tag_label: string (required) - The tag configuration label (e.g., "Role", "Priority")
 * - value: string (required) - The tag value text
 * - is_active: boolean (optional) - Whether the tag is active (default: true)
 * - description: string (optional) - Description for the tag value
 * 
 * Example CSV:
 * tag_label,value,is_active,description
 * Role,Admin User,true,Administrator role
 * Role,Regular User,true,Standard user role
 * Priority,High Priority,true,High priority tickets
 */

// CSV parsing function (copied from CSVUtils.ts)
const parseCSV = (csv: string) => {
  // Remove UTF-8 BOM if present
  const cleanCsv = csv.charCodeAt(0) === 0xFEFF ? csv.slice(1) : csv;
  const rows = cleanCsv.replaceAll('\r', '').split('\n').map(l => split_line_to_row(l))
  // Filter out empty rows (rows with no content or only whitespace)
  return rows.filter(row => {
    if (!row || row.length === 0) return false;
    return row.some(cell => cell && cell.trim() !== '');
  });
}

const split_line_to_row = (l: string) => {
  l = l.replace(/(".*?")/g, (match) => {
      return match.replace(/,/g, '<comma>').replace(/"/g, '');
  });

  let row = []

  const escape_sequence = '"\\""'
  if (l.includes(escape_sequence)) {
    const split_array = []
    while (l && l.includes(',')) {
      var comma_index = l.indexOf(',')
      const escape_sequence_index = l.indexOf(escape_sequence)
      if (escape_sequence_index < comma_index) {
        const closing_index = l.indexOf(escape_sequence, escape_sequence_index + 1)
        comma_index = l.indexOf(',', closing_index)
      }
      if (comma_index === -1) {
        split_array.push(l)
        break
      }
      split_array.push(l.substring(0,comma_index))
      l = l.substring(comma_index + 1)         
    }
    row = split_array
  }
  else row = l.split(',')
  
  row = row.map(e => e.replace(/<comma>/g, ','))

  return row
}

export interface CSVFieldSchema {
  name: string;
  required?: boolean;
  type?: 'string' | 'number' | 'boolean' | 'date';
  validator?: (value: any) => boolean | string; // returns true if valid, error message if invalid
}

export interface CSVSchema {
  fields: CSVFieldSchema[];
  allowExtraFields?: boolean;
  minRows?: number;
  maxRows?: number;
}

export interface CSVUploadButtonProps {
  schema: CSVSchema;
  onDataLoaded: (data: any[], errors: string[]) => void;
  onError?: (error: string) => void;
  onLoadingStart?: () => void;
  onLoadingEnd?: () => void;
  tooltipText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  style?: React.CSSProperties;
}

export const CSVUploadButton: React.FC<CSVUploadButtonProps> = ({
  schema,
  onDataLoaded,
  onError,
  onLoadingStart,
  onLoadingEnd,
  tooltipText = 'Upload CSV file',
  disabled = false,
  size = 'medium',
  style
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps>();

  const validateField = (value: any, fieldSchema: CSVFieldSchema): string | null => {
    // Check if required field is empty
    if (fieldSchema.required && (value === undefined || value === null || value === '')) {
      return `Field '${fieldSchema.name}' is required`;
    }

    // Skip validation if value is empty and not required
    if (!fieldSchema.required && (value === undefined || value === null || value === '')) {
      return null;
    }

    // Type validation
    if (fieldSchema.type) {
      switch (fieldSchema.type) {
        case 'number':
          if (isNaN(Number(value))) {
            return `Field '${fieldSchema.name}' must be a number`;
          }
          break;
        case 'boolean':
          const boolValue = String(value).toLowerCase();
          if (!['true', 'false', '1', '0', 'yes', 'no'].includes(boolValue)) {
            return `Field '${fieldSchema.name}' must be a boolean value`;
          }
          break;
        case 'date':
          if (isNaN(Date.parse(value))) {
            return `Field '${fieldSchema.name}' must be a valid date`;
          }
          break;
      }
    }

    // Custom validator
    if (fieldSchema.validator) {
      const validationResult = fieldSchema.validator(value);
      if (validationResult !== true) {
        return typeof validationResult === 'string' ? validationResult : `Field '${fieldSchema.name}' is invalid`;
      }
    }

    return null;
  };

  const validateCSV = (rows: string[][]): { data: any[], errors: string[] } => {
    const errors: string[] = [];
    const data: any[] = [];

    if (rows.length === 0) {
      errors.push('CSV file is empty');
      return { data, errors };
    }

    // Validate row count
    if (schema.minRows && rows.length < schema.minRows) {
      errors.push(`CSV must have at least ${schema.minRows} rows`);
    }
    if (schema.maxRows && rows.length > schema.maxRows) {
      errors.push(`CSV must have at most ${schema.maxRows} rows`);
    }

    const headers = rows[0];
    const dataRows = rows.slice(1);

    // Validate headers
    const requiredFields = schema.fields.filter(f => f.required);
    const missingRequiredHeaders = requiredFields.filter(field => 
      !headers.some(header => header.toLowerCase() === field.name.toLowerCase())
    );

    if (missingRequiredHeaders.length > 0) {
      errors.push(`Missing required headers: ${missingRequiredHeaders.map(f => f.name).join(', ')}`);
    }

    // Check for unknown fields (only once)
    if (!schema.allowExtraFields) {
      const unknownHeaders = headers.filter(header => 
        !schema.fields.some(field => field.name.toLowerCase() === header.toLowerCase())
      );
      if (unknownHeaders.length > 0) {
        errors.push(`Unknown fields: ${unknownHeaders.join(', ')}`);
      }
    }

    // Process data rows
    dataRows.forEach((row, rowIndex) => {
      const rowData: any = {};
      const rowErrors: string[] = [];

      // Map headers to field schemas
      headers.forEach((header, colIndex) => {
        const fieldSchema = schema.fields.find(f => 
          f.name.toLowerCase() === header.toLowerCase()
        );

        if (fieldSchema) {
          const value = row[colIndex];
          const validationError = validateField(value, fieldSchema);
          if (validationError) {
            rowErrors.push(`Row ${rowIndex + 2}: ${validationError}`);
          } else {
            // Convert value based on type
            let convertedValue: any = value;
            if (fieldSchema.type === 'number') {
              convertedValue = Number(value);
            } else if (fieldSchema.type === 'boolean') {
              const boolValue = String(value).toLowerCase();
              convertedValue = ['true', '1', 'yes'].includes(boolValue);
            } else if (fieldSchema.type === 'date') {
              convertedValue = new Date(value);
            }
            rowData[fieldSchema.name] = convertedValue;
          }
        } else {
          // Allow extra fields (unknown field errors are handled at header level)
          rowData[header] = row[colIndex];
        }
      });

      // Add row errors to overall errors
      errors.push(...rowErrors);

      // Only add row to data if it has no errors
      if (rowErrors.length === 0) {
        data.push(rowData);
      }
    });

    return { data, errors };
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
      const error = 'Please select a valid CSV file';
      showError(error);
      onError?.(error);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvString = e.target?.result as string;
        if (typeof csvString !== 'string') {
          throw new Error('Failed to read file');
        }

        const rows = parseCSV(csvString);
        const { data, errors } = validateCSV(rows);

        onDataLoaded(data, errors);

        if (errors.length > 0) {
          const errorMsg = `CSV validation failed:\n${errors.join('\n')}`;
          showError(errorMsg);
          onError?.(errorMsg);
        }
      } catch (error) {
        console.error('Failed to parse CSV file:', error);
        const errorMsg = 'Failed to parse CSV file';
        showError(errorMsg);
        onError?.(errorMsg);
      } finally {
        onLoadingEnd?.();
      }
    };

    reader.onerror = () => {
      const error = 'Failed to read file';
      showError(error);
      onError?.(error);
      onLoadingEnd?.();
    };

    reader.readAsText(file);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    onLoadingStart?.();
    fileInputRef.current?.click();
  };

  const showError = (message: string) => {
    setDialogProps({
      title: 'CSV Upload Error',
      content: <Alert severity="error" style={{ marginBottom: 16, whiteSpace: 'pre-line' }}>
        {message}
        </Alert>,
      onClose: () => setDialogProps(undefined),
      closeLabel: 'OK'
    });
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <Tooltip title={tooltipText}>
        <IconButton
          onClick={handleClick}
          disabled={disabled}
          size={size}
          style={style}
        >
          <img 
            src={textIcon} 
            alt="CSV Upload" 
            style={{ 
              width: size === 'small' ? '20px' : '24px',
              height: size === 'small' ? '20px' : '24px',
              filter: disabled ? 'grayscale(100%) opacity(50%)' : 'none'
            }} 
          />
        </IconButton>
      </Tooltip>

      {dialogProps && (
        <StepConfirmation
          confirmationDialogs={[dialogProps]}
          finish={() => { }}
          cancel={() => setDialogProps(undefined)}
          isValid={true}
        />
      )}
    </>
  );
}; 